'use client';

/**
 * CaseDetailCard
 *
 * Renders the structured payload returned by `case_management_get_case`.
 * A case IS a loan on this platform — sections cover borrower, loan
 * details, status, and timeline.
 *
 * Issue #1432 Phase 2 — Wave 1 P0
 */

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { z } from '@ops-matrix/shared-types';
import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { GlassChip } from '../GlassChip';
import { RendererEmptyState } from './RendererEmptyState';
import { formatStatusLabel, getStatusColor } from './statusColor';
import type { AstorToolRendererProps } from './types';

// ============================================================================
// Schema (lenient)
// ============================================================================

const CaseDetailSchema = z.object({
  id: z.string(),
  caseNumber: z.string().optional(),
  borrowerName: z.string().optional(),
  borrowerEmail: z.string().optional(),
  borrowerPhone: z.string().optional(),
  amount: z.number().optional(),
  interestRate: z.number().optional(),
  term: z.string().optional(),
  propertyAddress: z.string().optional(),
  status: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
});

const CaseWrapperSchema = z.object({
  case: CaseDetailSchema,
});

type CaseDetail = z.infer<typeof CaseDetailSchema>;

function parseCaseData(data: unknown): CaseDetail | null {
  const wrapped = CaseWrapperSchema.safeParse(data);
  if (wrapped.success) return wrapped.data.case;

  const direct = CaseDetailSchema.safeParse(data);
  if (direct.success) return direct.data;

  return null;
}

// ============================================================================
// Helpers
// ============================================================================

function formatMaybeDate(value: string | undefined, fmt: (d: Date | string) => string): string {
  if (!value) return '';
  try {
    return fmt(value);
  } catch {
    return value;
  }
}

// ============================================================================
// Subcomponents
// ============================================================================

interface FieldRowProps {
  label: string;
  children: ReactNode;
}

const FieldRow = ({ label, children }: FieldRowProps): ReactElement => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      py: 0.5,
    }}
  >
    <Typography variant="body2" sx={{ color: 'text.secondary', flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>{children}</Box>
  </Box>
);

interface SectionProps {
  title: string;
  children: ReactNode;
}

const Section = ({ title, children }: SectionProps): ReactElement => (
  <Box>
    <Typography variant="h6" sx={{ mb: 1 }}>
      {title}
    </Typography>
    <Stack spacing={0.25}>{children}</Stack>
  </Box>
);

// ============================================================================
// Component
// ============================================================================

export const CaseDetailCard = ({ data }: AstorToolRendererProps): ReactElement => {
  const { t, formatCurrency, formatDate } = useI18n();
  const parsed = useMemo(() => parseCaseData(data), [data]);

  if (!parsed) {
    return (
      <RendererEmptyState
        title={t('astorTools.case.notFound')}
        description={t('astorTools.case.notFoundDescription')}
      />
    );
  }

  const hasBorrower = parsed.borrowerName || parsed.borrowerEmail || parsed.borrowerPhone;
  const hasLoan = parsed.amount != null || parsed.interestRate != null || parsed.term || parsed.propertyAddress;
  const hasTimeline = parsed.createdAt || parsed.updatedAt;
  const status = parsed.status?.trim();

  return (
    <Card variant="outlined" sx={{ my: 1.5, borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={2.5}>
          {hasBorrower && (
            <Section title={t('astorTools.case.borrowerSection')}>
              {parsed.borrowerName && (
                <FieldRow label={t('astorTools.case.borrowerName')}>
                  <Typography variant="body1">{parsed.borrowerName}</Typography>
                </FieldRow>
              )}
              {parsed.borrowerEmail && (
                <FieldRow label={t('astorTools.case.borrowerEmail')}>
                  <Typography variant="body1">{parsed.borrowerEmail}</Typography>
                </FieldRow>
              )}
              {parsed.borrowerPhone && (
                <FieldRow label={t('astorTools.case.borrowerPhone')}>
                  <Typography variant="body1">{parsed.borrowerPhone}</Typography>
                </FieldRow>
              )}
            </Section>
          )}

          {hasLoan && (
            <Section title={t('astorTools.case.loanSection')}>
              {typeof parsed.amount === 'number' && (
                <FieldRow label={t('astorTools.case.amount')}>
                  <Typography variant="body1">{formatCurrency(parsed.amount)}</Typography>
                </FieldRow>
              )}
              {typeof parsed.interestRate === 'number' && (
                <FieldRow label={t('astorTools.case.rate')}>
                  <Typography variant="body1">{parsed.interestRate.toFixed(2)}%</Typography>
                </FieldRow>
              )}
              {parsed.term && (
                <FieldRow label={t('astorTools.case.term')}>
                  <Typography variant="body1">{parsed.term}</Typography>
                </FieldRow>
              )}
              {parsed.propertyAddress && (
                <FieldRow label={t('astorTools.case.property')}>
                  <Typography variant="body1">{parsed.propertyAddress}</Typography>
                </FieldRow>
              )}
            </Section>
          )}

          {status && (
            <Section title={t('astorTools.case.statusSection')}>
              <Box>
                <GlassChip
                  label={formatStatusLabel(status)}
                  color={getStatusColor(status)}
                  size="small"
                  variant="outlined"
                />
              </Box>
            </Section>
          )}

          {hasTimeline && (
            <Section title={t('astorTools.case.timelineSection')}>
              {parsed.updatedAt && (
                <FieldRow label={t('astorTools.case.updated')}>
                  <Typography variant="body1">
                    {formatMaybeDate(parsed.updatedAt, (d) => formatDate(d, 'medium'))}
                  </Typography>
                </FieldRow>
              )}
              {parsed.createdAt && (
                <FieldRow label={t('astorTools.case.created')}>
                  <Typography variant="body1">
                    {formatMaybeDate(parsed.createdAt, (d) => formatDate(d, 'medium'))}
                  </Typography>
                </FieldRow>
              )}
            </Section>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default CaseDetailCard;
